import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, Mail, CheckCircle, XCircle, Send } from 'lucide-react'
import apiService from '../services/api'
import toast from 'react-hot-toast'

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  useEffect(() => {
    const emailFromState = (location.state as any)?.email
    if (emailFromState) {
      setEmail(emailFromState)
    }
  }, [location.state])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }
    if (!code.trim() || code.length !== 6) {
      setError('Please enter the 6-digit code from your email')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const response = await apiService.verifyEmail(email.trim().toLowerCase(), code.trim())

      if (response.success) {
        if (response.data?.tokens && response.data?.user) {
          const { tokens, user: userData } = response.data

          localStorage.setItem('authToken', tokens.accessToken)
          if (tokens.refreshToken) {
            localStorage.setItem('refreshToken', tokens.refreshToken)
          }
          localStorage.setItem('user', JSON.stringify(userData))

          setSuccess(true)
          toast.success('Email verified successfully!')

          try {
            const schoolsResponse = await apiService.getSchoolProfile().catch(() => ({ success: false, data: null }))
            const hasSchool = schoolsResponse.success && schoolsResponse.data?.school && Object.keys(schoolsResponse.data.school).length > 0
            setTimeout(() => {
              window.location.href = hasSchool ? '/dashboard' : '/create-school'
            }, 1500)
          } catch {
            setTimeout(() => { window.location.href = '/create-school' }, 1500)
          }
        } else {
          setSuccess(true)
          toast.success('Email verified successfully! Please login to continue.')
          setTimeout(() => navigate('/login', { replace: true, state: { message: 'Email verified successfully! Please login to continue.' } }), 2000)
        }
      } else {
        throw new Error(response.message || 'Verification failed')
      }
    } catch (err: any) {
      const errData = err.response?.data
      const errorMessage = (typeof errData?.error === 'object' ? errData.error?.message : errData?.error)
        || errData?.message
        || err.message
        || 'Invalid or expired verification code. Please try again.'
      const msg = String(errorMessage)
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendEmail = async () => {
    if (!email.trim()) {
      setError('Email address is required to resend the code')
      toast.error('Email address is required to resend the code')
      return
    }

    setError('')
    setResendSuccess(false)
    setIsResending(true)

    try {
      const response = await apiService.resendVerificationEmail(email.trim().toLowerCase())
      if (response.success) {
        setResendSuccess(true)
        toast.success('Verification code sent! Check your inbox.')
        setTimeout(() => setResendSuccess(false), 5000)
      } else {
        throw new Error(response.message || 'Failed to resend verification code')
      }
    } catch (err: any) {
      const errData = err.response?.data
      const errorMessage = (typeof errData?.error === 'object' ? errData.error?.message : errData?.error)
        || errData?.message
        || err.message
        || 'Failed to resend. Please try again.'
      const msg = String(errorMessage)
      setError(msg)
      toast.error(msg)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="mx-auto flex items-center justify-center mb-4">
            <img src="/logo.png" alt="BookMyYogaTTC Logo" className="h-10 sm:h-12 w-auto object-contain" />
          </div>
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="text-6xl mb-4">
            ✉️
          </motion.div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Verify Your Email</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter the 6-digit code we sent to your email address.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8"
        >
          {success ? (
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <div className="text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }} className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-10 w-10 text-primary-600" />
                </motion.div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
                <p className="text-gray-600 mb-6">Redirecting...</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <form onSubmit={handleVerify} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center space-x-2">
                    <XCircle className="h-5 w-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {resendSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-primary-600 flex-shrink-0" />
                    <span>Verification code sent! Check your inbox.</span>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900"
                    placeholder="you@example.com"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">6-digit verification code</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="code"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 text-center text-xl tracking-[0.5em] font-mono"
                      placeholder="000000"
                      disabled={isLoading}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Enter the code from the email we sent you.</p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email.trim() || code.length !== 6}
                  className="w-full flex justify-center items-center py-3 px-6 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Email'
                  )}
                </button>

                <div className="text-center pt-4 border-t border-gray-200 space-y-3">
                  <p className="text-sm text-gray-600 mb-2">Didn&apos;t receive the code?</p>
                  <button
                    type="button"
                    onClick={handleResendEmail}
                    disabled={isResending || !email.trim()}
                    className="inline-flex items-center space-x-2 text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Resend Code</span>
                      </>
                    )}
                  </button>
                  <div>
                    <Link to="/login" className="text-sm text-gray-600 hover:text-primary-600">
                      Back to Login
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default VerifyEmail
